/**
 * Environment detection by browser hostname.
 *
 * QA needs an at-a-glance signal of which deployment they're looking at, so
 * dev and UAT get a distinct page background. Production and local are left
 * untouched (return `null` → the default `--neutral-50` page background).
 *
 * Detection is purely runtime (hostname-based) so it needs no CI/build-arg
 * coordination and works the moment the page loads.
 */

export type AppEnvironment = 'dev' | 'uat' | 'production' | 'local';

/** Background color shown for each flagged environment. */
export const ENV_BACKGROUND_COLORS = {
  dev: '#f7f5bc', // yellow
  uat: '#acd8a7', // green
} as const;

/** Hostnames that identify the flagged (non-production) deployments. */
const ENV_HOSTNAMES = {
  dev: 'epappdev.elitepermits.com',
  uat: 'epapptest.elitepermits.com',
} as const;

export const getAppEnvironment = (): AppEnvironment => {
  const host =
    typeof window !== 'undefined' && window.location
      ? window.location.hostname.toLowerCase()
      : '';

  if (host === ENV_HOSTNAMES.dev) return 'dev';
  if (host === ENV_HOSTNAMES.uat) return 'uat';
  return 'production';
};

/**
 * Page background color for the current environment, or `null` when no
 * override applies (production and local — keep each surface's own default).
 */
export const getEnvBackground = (): string | null => {
  const env = getAppEnvironment();
  if (env === 'dev') return ENV_BACKGROUND_COLORS.dev;
  if (env === 'uat') return ENV_BACKGROUND_COLORS.uat;
  return null;
};

/**
 * CSS declaration that defines the `--app-bg` token — emitted ONLY for dev/UAT.
 * In production/local it returns '' so `--app-bg` stays undefined, and every
 * surface using `var(--app-bg, <original>)` falls back to its original color.
 * This keeps production pixel-for-pixel unchanged.
 */
export const getEnvBackgroundDeclaration = (): string => {
  const color = getEnvBackground();
  return color ? `--app-bg: ${color};` : '';
};
